import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cable, Network, Zap, BatteryCharging, Printer, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "fiber", label: "Optical Fiber Cables", icon: Cable },
  { id: "networking", label: "Networking Equipment", icon: Network },
  { id: "optical", label: "Optical Components", icon: Zap },
  { id: "power", label: "Power & Backup", icon: BatteryCharging },
  { id: "office", label: "Office & IT", icon: Printer },
];

const BLURBS = {
  fiber: "High-quality optical fiber cable solutions for telecom networks, enterprise connectivity, backbone infrastructure and access networks.",
  networking: "Switching and routing equipment for enterprise, access and aggregation network builds.",
  optical: "Optical transceivers, patch cords and fiber management components for complete link assemblies.",
  power: "UPS, SMPS and battery systems engineered for continuous telecom operation.",
  office: "Office IT equipment, peripherals and consumables for operational readiness.",
};

const ICONS = { fiber: Cable, networking: Network, optical: Zap, power: BatteryCharging, office: Printer };

const PRODUCTS = [
  { name: "6 Fiber OFC", cat: "fiber", spec: "6F · SINGLE-MODE" },
  { name: "12 Fiber OFC", cat: "fiber", spec: "12F · SINGLE-MODE" },
  { name: "24 Fiber OFC", cat: "fiber", spec: "24F · SINGLE-MODE" },
  { name: "48 Fiber OFC", cat: "fiber", spec: "48F · SINGLE-MODE" },
  { name: "96 Fiber OFC", cat: "fiber", spec: "96F · SINGLE-MODE" },
  { name: "Customized Fiber Configurations", cat: "fiber", spec: "CUSTOM BUILD" },
  { name: "Network Switches", cat: "networking", spec: "L2 · GIGABIT" },
  { name: "Managed Switches", cat: "networking", spec: "L2/L3 · MANAGED" },
  { name: "Unmanaged Switches", cat: "networking", spec: "PLUG & PLAY" },
  { name: "Routers", cat: "networking", spec: "WAN / ENTERPRISE" },
  { name: "Network Accessories", cat: "networking", spec: "ACCESSORIES" },
  { name: "Optical Network Equipment", cat: "networking", spec: "ONU · OLT" },
  { name: "SFP Modules", cat: "optical", spec: "1G · LC" },
  { name: "SFP+ Modules", cat: "optical", spec: "10G · LC" },
  { name: "Optical Transceivers", cat: "optical", spec: "MULTI-RATE" },
  { name: "Fiber Patch Cords", cat: "optical", spec: "SC/LC · SM/MM" },
  { name: "Optical Patch Cables", cat: "optical", spec: "SM / MM" },
  { name: "Fiber Connectors", cat: "optical", spec: "SC · LC · ST" },
  { name: "ODF / Fiber Management", cat: "optical", spec: "ODF · LIU" },
  { name: "Fiber Accessories", cat: "optical", spec: "SPLICING" },
  { name: "UPS Systems", cat: "power", spec: "ONLINE / LINE-INTERACTIVE" },
  { name: "SMPS", cat: "power", spec: "TELECOM GRADE" },
  { name: "Telecom Batteries", cat: "power", spec: "VRLA / LI-ION" },
  { name: "Power Backup Systems", cat: "power", spec: "BACKUP POWER" },
  { name: "DC Power Solutions", cat: "power", spec: "-48V DC" },
  { name: "Power Accessories", cat: "power", spec: "ACCESSORIES" },
  { name: "Printers", cat: "office", spec: "OFFICE" },
  { name: "Scanners", cat: "office", spec: "OFFICE" },
  { name: "Printer Cartridges", cat: "office", spec: "CONSUMABLES" },
  { name: "Computer Peripherals", cat: "office", spec: "IT HARDWARE" },
  { name: "Networking Accessories", cat: "office", spec: "IT HARDWARE" },
  { name: "Office IT Consumables", cat: "office", spec: "CONSUMABLES" },
];

export default function Products({ onQuote }) {
  const [cat, setCat] = useState("all");
  const visible = cat === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.cat === cat);

  return (
    <section id="products" data-testid="products-section" className="bg-slate-50 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHead
          eyebrow="Products"
          title="Telecom & Network Equipment Supply"
          sub="A focused catalogue of optical fiber, networking, optical component, power and office IT products for telecom and enterprise infrastructure."
        />

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Product categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`product-filter-${c.id}`}
                role="tab"
                aria-selected={cat === c.id}
                onClick={() => setCat(c.id)}
                className={`rounded-full px-4.5 px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  cat === c.id
                    ? "bg-navy text-white shadow-lg shadow-navy/25"
                    : "border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-electric"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {cat !== "all" && (
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-500">{BLURBS[cat]}</p>
          )}
        </Reveal>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => {
              const Icon = ICONS[p.cat];
              return (
                <motion.div
                  layout
                  key={p.name}
                  data-testid={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-blue-400/50 hover:shadow-xl hover:shadow-blue-500/10"
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <span className="inline-flex rounded-xl bg-navy/[0.05] p-2.5 text-electric transition-colors duration-300 group-hover:bg-electric group-hover:text-white">
                        <Icon size={18} />
                      </span>
                      <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[10px] font-semibold tracking-wider text-slate-500">
                        {p.spec}
                      </span>
                    </div>
                    <h3 className="mt-4 font-heading text-base font-bold tracking-tight text-navy">
                      {p.name}
                    </h3>
                  </div>
                  <button
                    data-testid={`product-quote-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => onQuote(p.name)}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-electric opacity-80 transition-all duration-200 hover:gap-2.5 hover:opacity-100"
                  >
                    Request Quote <ArrowUpRight size={13} />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.1}>
          <div className="mt-14 flex flex-col items-center justify-between gap-6 rounded-3xl border border-blue-100 bg-gradient-to-r from-blue-50 to-cyan-50 p-8 sm:flex-row lg:p-10">
            <p className="text-center font-heading text-lg font-bold text-navy sm:text-left sm:text-xl">
              Looking for a specific telecom or networking product?
            </p>
            <button
              data-testid="products-request-quote-button"
              onClick={() => onQuote()}
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-electric px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:bg-blue-500"
            >
              Request Product Quote
              <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
